/**
 * What may be used as an identifier in a composite storage key.
 *
 * Defined here, in `shared`, rather than beside the key builders in
 * `api/src/data/keys.ts` — because the console needs the same rule. A form that
 * accepts a tenant id the API will reject is a round trip to learn something the
 * browser could have said immediately, and a *second* copy of the pattern is a
 * place for the two answers to drift apart. `assertKeySafe` imports this one.
 *
 * The `#` is the reason this exists at all: tenant-owned items live in a
 * partition keyed `TENANT#{id}`, so an id containing `#` could forge a partition
 * boundary and reach another customer's records. The character class is an allow
 * list for that reason, not a stylistic preference.
 */
export const KEY_SAFE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
export const isKeySafe = (value) => KEY_SAFE_PATTERN.test(value);
/**
 * Why a tenant id was refused, in words a form can show.
 *
 * `isKeySafe` answers whether; this answers why, because "invalid" sends someone
 * to guess. Returns undefined when the value is fine.
 */
export const tenantIdProblem = (value) => {
    if (value === '')
        return 'A customer id is required.';
    if (value.length > 128)
        return 'A customer id may be at most 128 characters.';
    if (!/^[A-Za-z0-9]/.test(value))
        return 'A customer id must start with a letter or a digit.';
    if (!isKeySafe(value)) {
        return 'A customer id may contain only letters, digits, dots, hyphens and underscores.';
    }
    return undefined;
};
/**
 * What an email address must look like to be stored as a person's key.
 *
 * One rule for the staff access screen, a customer's own users screen and the
 * access store underneath both. There were three: the two validators accepted
 * `amit+test@qrvey.com` and the store then refused it, because it checked the
 * address against `KEY_SAFE_PATTERN` — an identifier rule with no `+` in it —
 * and the refusal surfaced as a 500 the console could only call an internal
 * error. Plus-addressing is ordinary, so the rule here admits it.
 *
 * What it keeps out is what could actually harm a key: `#`, which would forge
 * a boundary in `PRINCIPAL#{email}` exactly as it would in `TENANT#{id}`;
 * whitespace; and anything that is not one `@` between a local part and a
 * dotted domain. 254 is the longest address SMTP will carry.
 */
export const EMAIL_ADDRESS_PATTERN = /^[^\s@#]+@[^\s@#]+\.[^\s@#]+$/;
export const isEmailAddress = (value) => value.length <= 254 && EMAIL_ADDRESS_PATTERN.test(value);
//# sourceMappingURL=identifiers.js.map